import { Injectable, inject } from '@angular/core';
import { Observable, Subject, map } from 'rxjs';
import { NotificationHttpService } from './http/notification.http.service';
import { DeleteNotificationMapper } from '../../../mappers/system/notification/delete-notification.mapper';
import { GetNotificationsPaginatedMapper } from '../../../mappers/system/notification/get-notifications-paginated.mapper';
import { DataResponseModel } from '../../../models/apis/data-response.model';
import { StatusResponseModel } from '../../../models/apis/status-response.model';
import { GetNotificationsPaginatedModel } from '../../../models/apis/system/notification/get-notifications-paginated.model';
import { NotificationQueryModel } from '../../../models/apis/system/notification/notification-query.model';
import { GetUnreadNotificationsMapper } from '../../../mappers/system/notification/get-unread-notifications.mapper';
import { GetNotificationDownloadLinkMapper } from '../../../mappers/system/notification/get-notification-download-link.mapper';
import { GetNotificationInfoMapper } from '../../../mappers/system/notification/get-notification-info.mapper';
import { SetReadNotificationMapper } from '../../../mappers/system/notification/set-read-notification.mapper';
import { SetReadAllNotificationsMapper } from '../../../mappers/system/notification/set-read-all-notifications.mapper';
import { NotificationUnreadModel } from '../../../models/apis/system/notification/notification-unread.model';
import { NotificationInfoModel } from '../../../models/apis/system/notification/notification-info.model';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private readonly _notificationHttpService = inject(NotificationHttpService);
    private readonly _getNotificationsPaginatedMapper = inject(
        GetNotificationsPaginatedMapper
    );
    private readonly _getUnreadNotificationsMapper = inject(
        GetUnreadNotificationsMapper
    );
    private readonly _getNotificationDownloadLinkMapper = inject(
        GetNotificationDownloadLinkMapper
    );
    private readonly _getNotificationInfoMapper = inject(
        GetNotificationInfoMapper
    );
    private readonly _setReadNotificationMapper = inject(
        SetReadNotificationMapper
    );
    private readonly _setReadAllNotificationsMapper = inject(
        SetReadAllNotificationsMapper
    );
    private readonly _deleteNotificationMapper = inject(
        DeleteNotificationMapper
    );

    private readonly _lastNotificationInfo: Subject<NotificationInfoModel> = new Subject<NotificationInfoModel>();
    lastNotificationInfo = this._lastNotificationInfo.asObservable();

    getPaginated(
        request: GetNotificationsPaginatedModel
    ): Observable<DataResponseModel<NotificationQueryModel>> {
        return this._notificationHttpService
            .getPaginated(
                this._getNotificationsPaginatedMapper.convertTo(request)
            )
            .pipe(
                map((response) =>
                    this._getNotificationsPaginatedMapper.convertFrom(response)
                )
            );
    }

    getUnreads(): Observable<DataResponseModel<NotificationUnreadModel[]>> {
        return this._notificationHttpService
            .getUnreads()
            .pipe(
                map((response) =>
                    this._getUnreadNotificationsMapper.convertFrom(response)
                )
            );
    }

    getDownloadLink(id: string): Observable<DataResponseModel<string>> {
        return this._notificationHttpService
            .getDownloadLink(id)
            .pipe(
                map((response) =>
                    this._getNotificationDownloadLinkMapper.convertFrom(
                        response
                    )
                )
            );
    }

    getInfo(): Observable<DataResponseModel<NotificationInfoModel>> {
        return this._notificationHttpService
            .getInfo()
            .pipe(
                map((response) =>
                    this._getNotificationInfoMapper.convertFrom(response)
                )
            );
    }

    setRead(id: string): Observable<StatusResponseModel> {
        return this._notificationHttpService
            .setRead(id)
            .pipe(
                map((response) =>
                    this._setReadNotificationMapper.convertFrom(response)
                )
            );
    }

    setReadAll(): Observable<StatusResponseModel> {
        return this._notificationHttpService
            .setReadAll()
            .pipe(
                map((response) =>
                    this._setReadAllNotificationsMapper.convertFrom(response)
                )
            );
    }

    delete(id: string): Observable<StatusResponseModel> {
        return this._notificationHttpService
            .delete(id)
            .pipe(
                map((response) =>
                    this._deleteNotificationMapper.convertFrom(response)
                )
            );
    }

    setLastNotificationInfo(data: NotificationInfoModel) {
        this._lastNotificationInfo.next(data);
    }
}
